import { useEffect, useMemo, useState } from "react";
import FormActionBar from "../../../components/staff/FormActionBar";
import InputText from "../../../components/staff/InputText";
import { createLanguage } from "../../../Services/languge";
import type { ILanguage } from "../../../interfaces/models/ILanguage";
import { toastPromise, toastError } from "../../../utils/toast";

export type AddLanguageFormProps = {
  isActive?: boolean;
  onLanguageCreated: (language: ILanguage) => void;
  onCancel: () => void;
};

export default function AddLanguageForm({
  isActive = true,
  onLanguageCreated,
  onCancel,
}: AddLanguageFormProps) {
  const [name, setName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isActive) {
      setName("");
      setAbbreviation("");
    }
  }, [isActive]);

  const isDirty = Boolean(name.trim() || abbreviation.trim());

  const dirtyFieldLabels = useMemo(() => {
    const labels: string[] = [];
    if (name.trim()) {
      labels.push("Name");
    }
    if (abbreviation.trim()) {
      labels.push("Abbreviation");
    }
    return labels;
  }, [name, abbreviation]);

  const handleClose = () => {
    setName("");
    setAbbreviation("");
    onCancel();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toastError("Name is required.");
      return;
    }
    setSaving(true);
    try {
      const created = await toastPromise(
        createLanguage({
          name: name.trim(),
          abbreviation: abbreviation.trim(),
        }),
        {
          pending: "Saving language...",
          success: "Language created successfully.",
        }
      );
      onLanguageCreated(created);
      setName("");
      setAbbreviation("");
      onCancel();
    } catch {
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <InputText
        title="Name"
        required
        value={name}
        onChange={setName}
        placeholder="e.g. Spanish"
      />
      <InputText
        title="Abbreviation"
        value={abbreviation}
        onChange={setAbbreviation}
        placeholder="e.g. ES"
      />
      <FormActionBar
        mode="add"
        isDirty={isDirty}
        dirtyFieldLabels={dirtyFieldLabels}
        onSave={() => void handleSave()}
        disabled={saving}
        onNavigateBack={handleClose}
        className="!static top-auto z-10 mt-4 border-t border-gray-200 bg-transparent p-0 pt-4 shadow-none"
      />
    </div>
  );
}
